// The error the axios interceptor throws, and the pure mapping that builds it.
//
// Dependency-free and separate from `./axios-client`, which sits in a module
// cycle with `./auth`: a test importing this mapping must not drag that cycle
// in (see the note on `./invite-errors`).

import { Cause, Option, Runtime } from "effect";
import type { ApiError } from "@/types/Api";

// The HTTP status, the API's error `code`, and the guard's structured
// `extensions`, carried so callers downstream of the interceptor (which no
// longer see the original AxiosError) can classify by status and code rather
// than by message text. Message text is prose written for the user and is not
// a stable contract.
export class HttpError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
    readonly extensions?: Record<string, unknown>,
  ) {
    super(message);
  }
}

const FALLBACK_BY_STATUS: Record<number, string> = {
  400: "Bad request",
  401: "Not authorized",
  403: "Access denied",
  404: "Resource not found",
  405: "Method not allowed",
  422: "Validation error",
  429: "Too many requests - please try again later",
  500: "Internal server error",
};

const requestPath = (requestUrl?: string): string | undefined => {
  if (!requestUrl) return undefined;
  try {
    return new URL(requestUrl, "http://localhost").pathname;
  } catch {
    return requestUrl.split(/[?#]/, 1)[0] || undefined;
  }
};

export const toHttpError = (
  status: number,
  data?: ApiError,
  requestUrl?: string,
): HttpError => {
  const first = data?.errors?.[0];
  const extensions = first?.extensions as Record<string, unknown> | undefined;
  const fallback = FALLBACK_BY_STATUS[status] || "Something went wrong";
  const path = requestPath(requestUrl);
  return new HttpError(
    first?.message ||
      (path ? `${fallback} (HTTP ${status} ${path})` : fallback),
    status,
    extensions?.["code"] as string | undefined,
    extensions,
  );
};

// Codes the API answers with when it rejects the credentials themselves, as
// opposed to failing to look at them.
const SESSION_REJECTION_CODES = new Set([
  "INVALID_CREDENTIALS",
  "INVALID_TOKEN",
  "USER_SUSPENDED",
]);

/**
 * The client itself decided the session is over: no usable tokens, or the
 * server rejected them. Distinct from a failure that says nothing about the
 * session, so callers can tell "sign the user out" from "try again".
 */
export class SessionEndedError extends Error {}

/**
 * The tokens could not be refreshed for a reason that says nothing about the
 * session (a server or network failure). The cookies are untouched and the
 * request that needed the token was not sent, so the caller can retry.
 */
export class SessionRefreshUnavailableError extends Error {
  readonly status: number | undefined;
  constructor(cause: unknown) {
    super("Could not refresh the session - please try again", { cause });
    this.status = statusOf(cause);
  }
}

function statusOf(error: unknown): number | undefined {
  if (typeof error !== "object" || error === null) return undefined;
  return "status" in error && typeof error.status === "number"
    ? error.status
    : undefined;
}

/**
 * Whether a failed refresh means the server rejected the credentials: a 401,
 * or a rejection code on a status below 500. A 5xx, a network failure, a
 * timeout, or a 400/403 that carries no such code leaves the session standing;
 * a server that could not answer says nothing about the token, whatever code
 * its error body carries.
 */
export function isSessionRejection(error: unknown): boolean {
  if (error instanceof SessionEndedError) return true;
  const status = statusOf(error);
  if (status === 401) return true;
  if (status !== undefined && status >= 500) return false;
  if (typeof error !== "object" || error === null || !("code" in error))
    return false;
  return (
    typeof error.code === "string" && SESSION_REJECTION_CODES.has(error.code)
  );
}

/**
 * True for the typed client's RequestError: the request never reached the
 * server (offline, refused, or a token refresh that got no verdict). Also
 * when `Effect.runPromise` rejected with it wrapped in a FiberFailure.
 */
export const isTransportFailure = (error: unknown): boolean => {
  const failure = Runtime.isFiberFailure(error)
    ? Option.getOrUndefined(
        Cause.failureOption(error[Runtime.FiberFailureCauseId]),
      )
    : error;
  return (
    typeof failure === "object" &&
    failure !== null &&
    "_tag" in failure &&
    (failure._tag === "RequestError" ||
      (failure._tag === "Retryable" &&
        "detail" in failure &&
        failure.detail === "RequestError"))
  );
};
